import { useEffect, useState } from "react";
import { getMallas, editarMalla } from "./helpers/api.helper";
import {
  Heading,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  Stack,
  HStack,
} from "@chakra-ui/react";
import Loadingtable from "../layout/components/Loading";
import { useSelector } from "react-redux";
import { MdCheck, MdClose, MdOutlineCreate } from "react-icons/md";
import EliminarMalla from "./components/mallas/EliminarMalla";
import CrearMalla from "./components/mallas/CrearMalla";
import CambiarEstadoMalla from "./components/mallas/CambiarEstadoMalla";
import { existeDescripcionMalla } from "./components/mallas/mallasUtils";

const Mallas = () => {
  const [mallas, setMallas] = useState();
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingDesc, setEditingDesc] = useState("");
  const [savingId, setSavingId] = useState(null);
  const toast = useToast();
  const { user } = useSelector((state) => state.user);

  const fetchMallas = async () => {
    setLoading(true);
    const mallasData = await getMallas();
    if (mallasData.success) {
      setMallas(mallasData.data);
    } else {
      toast({
        title: "Error",
        description: "Error al cargar las mallas",
        duration: 3000,
        isClosable: true,
        status: "error",
      });
    }
    setLoading(false);
  };

  const handleStartEdit = (malla) => {
    setEditingId(malla.id);
    setEditingDesc(malla.descripcion);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDesc("");
  };

  const handleSaveEdit = async (id) => {
    if (!editingDesc.trim()) {
      return toast({
        title: "La descripción no puede estar vacía",
        status: "warning",
        isClosable: true,
        duration: 3000,
      });
    }
    if (existeDescripcionMalla(mallas, editingDesc, id)) {
      return toast({
        title: "Ya existe una malla con esa descripción",
        status: "error",
        isClosable: true,
        duration: 3000,
      });
    }
    setSavingId(id);
    const res = await editarMalla({
      id,
      descripcion: editingDesc.trim(),
      usuarioCreate: user.username,
    });
    if (res.success) {
      toast({
        title: "Malla actualizada correctamente",
        status: "success",
        isClosable: true,
        duration: 3000,
      });
      setEditingId(null);
      setEditingDesc("");
      fetchMallas();
    } else {
      toast({
        title: "Error al actualizar malla",
        description: res.message || "Error inesperado",
        status: "error",
        isClosable: true,
        duration: 3000,
      });
    }
    setSavingId(null);
  };

  useEffect(() => {
    fetchMallas();
  }, []);

  return (
    <Stack w="100%" flex={1} overflow="hidden">
      <HStack w="100%" justifyContent="space-between" px={5}>
        <Heading
          fontWeight="medium"
          size="xl"
          fontFamily="fonts.title"
          color="brand.gris_primario"
        >
          Mallas
        </Heading>
        {(user.idRol === "admin" ||
          user.idRol === "coorOp" ||
          user.idRol === "usuarioIT") && (
          <CrearMalla user={user} fetchMallas={fetchMallas} mallas={mallas} />
        )}
      </HStack>
      {loading && <Loadingtable />}
      {mallas ? (
        <TableContainer flex={1} overflowY="auto" minH={0}>
          <Table size="sm" variant="simple">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr borderBottom="2px solid black">
                <Th>Descripcion</Th>
                <Th>Activo / Inactivo</Th>
                {(user.idRol === "admin" ||
                  user.idRol === "coorOp" ||
                  user.idRol === "usuarioIT") && (
                  <Th textAlign={"center"}>Acciones</Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {mallas.map((malla) => (
                <Tr key={malla.id}>
                  <Td>
                    {editingId === malla.id ? (
                      <Input
                        size="sm"
                        value={editingDesc}
                        onChange={(e) => setEditingDesc(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit(malla.id);
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                        autoFocus
                        w="xs"
                      />
                    ) : (
                      malla.descripcion
                    )}
                  </Td>
                  <Td>
                    <CambiarEstadoMalla
                      malla={malla}
                      fetchMallas={fetchMallas}
                      user={user}
                    />
                  </Td>
                  {(user.idRol === "admin" ||
                    user.idRol === "coorOp" ||
                    user.idRol === "usuarioIT") && (
                    <Td textAlign={"center"}>
                      {editingId === malla.id ? (
                        <>
                          <Tooltip label="Guardar">
                            <IconButton
                              variant="link"
                              icon={<MdCheck fontSize="20px" />}
                              onClick={() => handleSaveEdit(malla.id)}
                              isLoading={savingId === malla.id}
                              _hover={{ color: "green.500" }}
                            />
                          </Tooltip>
                          <Tooltip label="Cancelar">
                            <IconButton
                              variant="link"
                              icon={<MdClose fontSize="20px" />}
                              onClick={handleCancelEdit}
                              _hover={{ color: "red.500" }}
                            />
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip label="Editar Malla">
                            <IconButton
                              variant="link"
                              isDisabled={user.idRol === "usuarioIT"}
                              icon={<MdOutlineCreate fontSize="20px" />}
                              onClick={() => handleStartEdit(malla)}
                              _hover={{ color: "brand.naranja" }}
                            />
                          </Tooltip>
                          <EliminarMalla
                            malla={malla}
                            fetchMallas={fetchMallas}
                            user={user}
                          />
                        </>
                      )}
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : null}
    </Stack>
  );
};

export default Mallas;
